/**
 * The application shell: brand, navigation, and the page frame.
 *
 * Navigation is built from the module registry, not hard-coded. That is what makes adding
 * a module a one-line change, and it is also what keeps a user from seeing a navigation
 * item that leads somewhere every route will refuse.
 */

import {
  AvatarPlaceholder,
  ButtonField,
  Icon2,
  Spine,
  SpineItem,
  SpineItemParent,
  SrOnly,
  Typography,
  WkIconAllSprite,
  WkIconFilledMoreSprite,
} from '@wk/components-v3-react16'
import { useMemo, useState, type ReactNode } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'

import { ROLES, ROLE_LABELS, useAuth, type Role } from '../auth/AuthProvider'
import { runtimeConfig } from '../config/runtime'
import type { ModuleRegistry, NavEntry } from '../modules/registry'
import { SelectInput } from '../ui/fields'
import { LoadingState } from '../ui/states'

/**
 * A visible, clearly-labelled role switcher for local development.
 *
 * Rendered only when the development identity is in use, which the configuration and the
 * API both refuse outside a local environment. Its purpose is to make the identity
 * obviously artificial: an unlabelled fake identity is how somebody demonstrates a
 * permission they do not actually have.
 */
function DevelopmentIdentityBar() {
  const { identity, assumeRole } = useAuth()
  const [workerId, setWorkerId] = useState(identity?.workerId ?? '')

  if (assumeRole === undefined) return null

  const active = identity?.roles[0] ?? ''

  return (
    <div className="devex-dev-identity" data-testid="development-identity-bar">
      <Typography>
        <span className="devex-dev-identity__label">
          <Icon2 name="wk-icon-caution" flowClass="devex-icon--small" />
          Local development identity &mdash; not a real sign-in
        </span>
      </Typography>
      <div className="devex-dev-identity__controls">
        <SelectInput
          label="Role"
          value={active}
          onChange={(next) => {
            assumeRole([next as Role], workerId === '' ? undefined : workerId)
          }}
        >
          <option value="">Choose a role</option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS[role]}
            </option>
          ))}
        </SelectInput>
        <label className="devex-dev-identity__worker">
          <span>Worker</span>
          <input
            type="text"
            value={workerId}
            placeholder="W100006"
            onChange={(event) => setWorkerId(event.target.value)}
            onBlur={() => {
              if (identity !== undefined && identity.roles.length > 0) {
                assumeRole(
                  identity.roles,
                  workerId === '' ? undefined : workerId,
                )
              }
            }}
          />
        </label>
      </div>
    </div>
  )
}

/**
 * The navigation, built from the module registry.
 *
 * The design system's spine distributes its children by *slot*, not by position: it reads
 * the elements whose `slot` is `spineItem` and ignores everything else. A `SpineItem`
 * passed as a plain child is therefore silently dropped -- it renders nothing, logs
 * nothing, and leaves the overflow button as the only visible navigation. Hence the
 * wrapping `div`s below, which look redundant and are not.
 *
 * A group is a `SpineItemParent`. It renders its own heading from its `label` and
 * `iconName`, so the children are only the items beneath it, each in a `spineItemChild`
 * slot. Rendering a separate heading inside it would produce two.
 */
function Navigation({ entries }: { readonly entries: readonly NavEntry[] }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [expandedGroups, setExpandedGroups] = useState<readonly string[]>([])

  // Grouped items render inside a collapsible parent; ungrouped items render at the top
  // level. The order of first appearance is preserved, so registration order is the
  // navigation order.
  const { topLevel, groups } = useMemo(() => {
    const top: NavEntry[] = []
    const grouped = new Map<
      string,
      { group: NonNullable<NavEntry['group']>; items: NavEntry[] }
    >()
    for (const entry of entries) {
      if (entry.group === undefined) {
        top.push(entry)
        continue
      }
      const existing = grouped.get(entry.group.id)
      if (existing === undefined) {
        grouped.set(entry.group.id, { group: entry.group, items: [entry] })
      } else {
        existing.items.push(entry)
      }
    }
    return { topLevel: top, groups: [...grouped.values()] }
  }, [entries])

  /**
   * Whether a path is the one being viewed.
   *
   * Prefix-matching on a path segment, so the allocations item stays selected while its
   * editor drawer is open at `/allocations/:workerId/edit`. Matching the bare prefix
   * without the separator would also select `/allocations-archive`, which is a different
   * area.
   */
  const isActive = (to: string): boolean =>
    location.pathname === to || location.pathname.startsWith(`${to}/`)

  return (
    <Spine isExpanded treeId="devex-navigation">
      {topLevel.map(({ item }) => (
        <div slot="spineItem" key={item.id}>
          <SpineItem
            elementId={`nav-${item.id}`}
            label={item.label}
            iconName={item.iconName}
            {...(item.activeIconName === undefined
              ? {}
              : { activeIconName: item.activeIconName })}
            isSelected={isActive(item.to)}
            href={item.to}
            onItemClick={() => navigate(item.to)}
          />
        </div>
      ))}
      {groups.map(({ group, items }) => {
        const expanded = expandedGroups.includes(group.id)
        return (
          <div slot="spineItem" key={group.id}>
            <SpineItemParent
              elementId={`nav-group-${group.id}`}
              label={group.label}
              iconName={group.iconName}
              isExpanded={expanded}
              onParentItemClick={() =>
                setExpandedGroups((current) =>
                  current.includes(group.id)
                    ? current.filter((id) => id !== group.id)
                    : [...current, group.id],
                )
              }
            >
              {items.map(({ item }) => (
                <div slot="spineItemChild" key={item.id}>
                  <SpineItem
                    elementId={`nav-${item.id}`}
                    label={item.label}
                    iconName={item.iconName}
                    isSelected={isActive(item.to)}
                    href={item.to}
                    onItemClick={() => navigate(item.to)}
                  />
                </div>
              ))}
            </SpineItemParent>
          </div>
        )
      })}
    </Spine>
  )
}

/** Page eyebrow, title, and the row of contextual actions. */
export function PageHeader({
  eyebrow,
  title,
  actions,
  meta,
}: {
  readonly eyebrow?: string
  readonly title: string
  readonly actions?: ReactNode
  readonly meta?: ReactNode
}) {
  return (
    <header className="devex-page-header">
      <div className="devex-page-header__titles">
        {eyebrow !== undefined && (
          <Typography>
            <p className="devex-page-header__eyebrow">{eyebrow}</p>
          </Typography>
        )}
        <Typography>
          {/* The only h1 on the page, and the target of the skip link. */}
          <h1
            className="devex-page-header__title"
            id="page-title"
            tabIndex={-1}
          >
            {title}
          </h1>
        </Typography>
        {meta !== undefined && (
          <div className="devex-page-header__meta">{meta}</div>
        )}
      </div>
      {actions !== undefined && (
        <div className="devex-page-header__actions">{actions}</div>
      )}
    </header>
  )
}

export function AppShell({ registry }: { readonly registry: ModuleRegistry }) {
  const config = useMemo(() => runtimeConfig(), [])
  const { status, identity, signIn, signOut } = useAuth()

  const entries = useMemo(
    () => registry.navigation(config, identity?.roles ?? []),
    [registry, config, identity],
  )

  if (status === 'initialising') {
    return (
      <div className="devex-boot">
        <LoadingState label="Signing you in" />
      </div>
    )
  }

  return (
    <div className="devex-shell">
      {/* Every functional icon in one sprite, included exactly once. Two sprite
          components covering overlapping icons would log duplicate warnings. The kebab
          (wk-icon-filled-more) is not part of the all-icon bundle, so it is the one icon
          added individually. */}
      <WkIconAllSprite />
      <WkIconFilledMoreSprite />

      <a className="devex-skip-link" href="#page-title">
        Skip to content
      </a>

      <aside className="devex-shell__sidebar">
        <Link to="/" className="devex-brand">
          <AvatarPlaceholder initials="DS" size="small" srName="Devsphere" />
          <span className="devex-brand__text">
            <Typography>
              <span className="devex-brand__title">Devsphere</span>
            </Typography>
          </span>
        </Link>
        {entries.length > 0 ? (
          <Navigation entries={entries} />
        ) : (
          <div className="devex-shell__no-nav">
            <Typography>
              <p>No areas are available to your role.</p>
            </Typography>
          </div>
        )}
      </aside>

      <div className="devex-shell__body">
        <div className="devex-shell__topbar">
          <SrOnly>
            <span>Signed in as</span>
          </SrOnly>
          {identity === undefined ? (
            <ButtonField type="primary" size="small">
              <button type="button" onClick={() => void signIn()}>
                Sign in
              </button>
            </ButtonField>
          ) : (
            <div className="devex-shell__identity">
              <Typography>
                <span className="devex-shell__identity-name">
                  {identity.displayName}
                </span>
              </Typography>
              <Typography>
                <span className="devex-shell__identity-roles">
                  {identity.roles.map((role) => ROLE_LABELS[role]).join(', ') ||
                    'No role assigned'}
                </span>
              </Typography>
              <ButtonField type="text" size="small">
                <button type="button" onClick={() => void signOut()}>
                  Sign out
                </button>
              </ButtonField>
            </div>
          )}
        </div>

        <DevelopmentIdentityBar />

        <main className="devex-shell__main">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
